import React, { useCallback, useMemo } from 'react';
import { useTranslation } from '@rbx/intl';
import { APIReferenceMember } from '@modules/client';
import { ApiTag, CloudTag, EngineTag, ReferenceClass } from '@modules/ui/tags/types';
import useReferenceMemberStyles from '@modules/miscellaneous/common/styles/referenceMember.styles';
import '@rbx/ui';
import {
  getTagDescriptionTranslKey,
  getTagLabelTranslKey,
  hasDeprecatedTag,
  isDeprecatedTag,
} from '../utils/tagUtils';
import ReferenceMemberTag, { ReferenceMemberTagProps } from './ReferenceMemberTag';
import ReferenceMemberTitle from './ReferenceMemberTitle';

const getAugmentedTags = (
  tags: Array<CloudTag | EngineTag>,
  referenceClass: ReferenceClass,
  member?: APIReferenceMember,
): Array<CloudTag | EngineTag> => {
  // a deprecated tag can be missing now, while the member is deprecated
  // -> add it to the tags, if needed
  if (member?.deprecationMessage && !hasDeprecatedTag(tags)) {
    tags.push(referenceClass === 'cloud' ? CloudTag.Deprecated : ApiTag.Deprecated);
  }
  return tags;
};

const ReferenceMemberTags = ({
  className,
  member,
  memberName,
  tags = [],
  referenceClass = 'engine',
  engineRevampEnabled = false,
}: {
  className?: string;
  tags?: Array<CloudTag | EngineTag>;
  // one of these should be defined:
  memberName?: string;
  member?: APIReferenceMember;
  referenceClass?: ReferenceClass;
  engineRevampEnabled?: boolean;
}) => {
  const { ready, translate, translateHTML } = useTranslation();
  const {
    classes: { tags: tagsStyle },
    cx,
  } = useReferenceMemberStyles();

  const getDeprecatedTagDescription = useCallback(
    (_member?: APIReferenceMember, _memberName?: string) => {
      if (!_member && !_memberName) throw new Error('member or memberName must be defined');
      return translateHTML('Description.Deprecated', [
        {
          opening: 'nameStart',
          closing: 'nameEnd',
          // eslint-disable-next-line react/no-unstable-nested-components
          content: () => (_member ? <ReferenceMemberTitle member={_member} /> : _memberName),
        },
      ]);
    },
    [translateHTML],
  );
  const tagsData: Array<ReferenceMemberTagProps> = useMemo(
    () =>
      getAugmentedTags(tags, referenceClass, member).map((tag) => {
        const labelTranslKey = getTagLabelTranslKey(tag, referenceClass);
        if (!labelTranslKey)
          throw new Error(
            `No label translation key found for: ${referenceClass} tag ${tag} in either suffix map or translations`,
          );
        const descTranslKey = getTagDescriptionTranslKey(tag, referenceClass);
        if (!descTranslKey)
          throw new Error(
            `No description translation key found for ${referenceClass} tag ${tag} in either suffix map or translations`,
          );
        const description = isDeprecatedTag(tag)
          ? getDeprecatedTagDescription(member, memberName)
          : translate(descTranslKey);
        return {
          label: labelTranslKey ? translate(labelTranslKey) : labelTranslKey,
          description,
          tag,
          referenceClass,
        };
      }),
    [tags, member, memberName, getDeprecatedTagDescription, translate, referenceClass],
  );

  if (!ready || !tagsData.length) return null;

  return (
    <div
      className={cx(tagsStyle, className)}
      style={engineRevampEnabled ? { marginTop: 0 } : undefined}>
      {tagsData.map((tagProps) => (
        <ReferenceMemberTag
          key={tagProps.label}
          {...tagProps}
          overrideSeverity={engineRevampEnabled ? 'default' : undefined}
          engineRevampEnabled={engineRevampEnabled}
        />
      ))}
    </div>
  );
};

export default ReferenceMemberTags;
